import { useSmartProp } from '@smartfire/js-block-engine';
import {
    PanelBody,
    PanelRow,
    TextControl,
    ToggleControl,
    SelectControl,
    Button as WPButton,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { Skeleton } from '@mui/material';
import he from 'he';

export const PostTypesControl = ({ prefix = '' }) => {
    const [postType, setPostType] = useSmartProp(prefix + 'postType');

    let { postTypes } = useSelect((select) => {
        return {
            postTypes: select('core').getPostTypes({ per_page: -1 }),
        };
    });

    if (postTypes && Array.isArray(postTypes)) {
        postTypes = postTypes.filter((el) => el.viewable).map(getSlugAndName);
        postTypes.unshift({ value: '', label: '--- Choissez un type de publication ---' });
    }

    return postTypes && postTypes.length > 0 ? (
        <SelectControl label="Post Type" value={postType} onChange={setPostType} options={postTypes} />
    ) : (
        <Skeleton variant="rectangular" width={200} height={40} />
    );
};

export const PostControl = ({ postType, postId, onChange }) => {
    let { posts } = useSelect((select) => {
        var fields = 'id,title';
        if (postType === 'product') fields += ',sku';

        return {
            posts: postType
                ? select('core').getEntityRecords('postType', postType, {
                      per_page: -1,
                      _fields: fields,
                      status: 'publish',
                      orderby: 'title',
                      order: 'asc',
                  })
                : [],
        };
    });

    if (posts && Array.isArray(posts)) {
        posts = posts.map(getIdAndTitle);
        posts.unshift({ value: '', label: '--- Choissez une publication ---' });
    }

    if (!postType) return null;
    return posts && posts.length > 0 ? (
        <SelectControl label="Post" value={postId} onChange={onChange} options={posts} />
    ) : (
        <Skeleton variant="rectangular" width={200} height={40} />
    );
};

export const LinkControl = ({ prefix = '', title = 'Lien' }) => {
    const [text, setText] = useSmartProp(prefix + 'text');
    const [linkType, setLinkType] = useSmartProp(prefix + 'linkType');
    const [link, setLink] = useSmartProp(prefix + 'link');
    const [postType] = useSmartProp(prefix + 'postType');
    const [postId, setPostId] = useSmartProp(prefix + 'postId');
    const [taxonomy, setTaxonomy] = useSmartProp(prefix + 'taxonomy');
    const [termId, setTermId] = useSmartProp(prefix + 'termId');
    const [fileId, setFileId] = useSmartProp(prefix + 'fileId');
    const [targetBlank, setTargetBlank] = useSmartProp(prefix + 'targetBlank');

    const resetProps = () => {
        setPostId(0);
        setTermId(0);
        setFileId(0);
        setLink('');
    };

    const onSelectFile = (file) => {
        resetProps();
        setFileId(file.id);
        setLink(file.url);
    };

    const onChangePostId = (value) => {
        resetProps();
        setPostId(parseInt(value));
    };

    const onChangeTermId = (value) => {
        resetProps();
        setTermId(parseInt(value));
    };

    let { file, pages, taxonomies, terms } = useSelect((select) => {
        return {
            file: fileId ? select('core').getMedia(fileId) : undefined,
            pages: select('core').getEntityRecords('postType', 'page', {
                per_page: -1,
                _fields: 'id,title',
                orderby: 'title',
                order: 'asc',
                status: 'publish',
            }),
            taxonomies: postType ? select('core').getTaxonomies({ type: postType }) : [],
            terms: taxonomy
                ? select('core').getEntityRecords('taxonomy', taxonomy, {
                      per_page: -1,
                      _fields: 'id,name',
                      orderby: 'name',
                      order: 'asc',
                  })
                : [],
        };
    });

    if (pages && Array.isArray(pages)) {
        pages = pages.map(getIdAndTitle);
        pages.unshift({ value: '', label: '--- Choissez une page ---' });
    }

    if (taxonomies && Array.isArray(taxonomies)) {
        taxonomies = taxonomies.map(getSlugAndName);
        taxonomies.unshift({ value: '', label: '--- Choissez une taxonomie ---' });
    }

    if (terms && Array.isArray(terms)) {
        terms = terms.map(getIdAndName);
        terms.unshift({ value: '', label: '--- Choissez un terme ---' });
    }

    return (
        <PanelBody title={title} initialOpen={true}>
            <PanelRow>
                <TextControl label="Texte" value={text} onChange={setText} />
            </PanelRow>
            <PanelRow>
                <SelectControl
                    label="Type de lien"
                    value={linkType}
                    onChange={(value) => {
                        setLinkType(value);
                        setLink('');
                        setFileId(0);
                    }}
                    options={[
                        { value: 'page', label: 'Page' },
                        { value: 'post', label: 'Post' },
                        { value: 'term', label: 'Catégorie' },
                        { value: 'file', label: 'Fichier' },
                        { value: 'email', label: 'Email' },
                        { value: 'tel', label: 'Téléphone' },
                        { value: 'other', label: 'Autre' },
                    ]}
                    style={{ width: '100%' }}
                />
            </PanelRow>
            <PanelRow>
                {
                    {
                        page:
                            pages && pages.length > 0 ? (
                                <SelectControl label="Page" value={postId} onChange={onChangePostId} options={pages} />
                            ) : (
                                <Skeleton variant="rectangular" width={200} height={40} />
                            ),
                        post: <PostTypesControl prefix={prefix} />,
                        term: <PostTypesControl prefix={prefix} />,
                        file: (
                            <MediaUploadCheck>
                                <MediaUpload
                                    onSelect={onSelectFile}
                                    allowedTypes={['application']}
                                    value={fileId}
                                    render={({ open }) => (
                                        <WPButton
                                            className={'editor-post-featured-image__toggle'}
                                            onClick={open}
                                            variant="secondary"
                                        >
                                            {fileId && file ? (
                                                <>
                                                    {file.title.rendered}
                                                    <br />
                                                    Remplacer le fichier
                                                </>
                                            ) : (
                                                'Choisir un fichier'
                                            )}
                                        </WPButton>
                                    )}
                                />
                            </MediaUploadCheck>
                        ),
                        email: <TextControl label="Email" type="email" value={link} onChange={setLink} />,
                        tel: <TextControl label="Téléphone" type="tel" value={link} onChange={setLink} />,
                        other: <TextControl label="Lien" value={link} onChange={setLink} />,
                    }[linkType]
                }
            </PanelRow>
            {linkType === 'social' && (
                <PanelRow>
                    <TextControl label="Lien" value={link} onChange={setLink} />
                </PanelRow>
            )}
            {linkType === 'post' && postType && (
                <PanelRow>
                    <PostControl postType={postType} postId={postId} onChange={onChangePostId} />
                </PanelRow>
            )}
            {linkType === 'term' &&
                postType &&
                (taxonomies && taxonomies.length > 0 ? (
                    <PanelRow>
                        <SelectControl label="Taxonomie" value={taxonomy} onChange={setTaxonomy} options={taxonomies} />
                    </PanelRow>
                ) : (
                    <Skeleton variant="rectangular" width={200} height={40} />
                ))}
            {linkType === 'term' &&
                postType &&
                taxonomy &&
                (terms && terms.length > 0 ? (
                    <PanelRow>
                        <SelectControl label="Catégorie" value={termId} onChange={onChangeTermId} options={terms} />
                    </PanelRow>
                ) : (
                    <Skeleton variant="rectangular" width={200} height={40} />
                ))}
            <PanelRow>
                <ToggleControl label="Ouvrir dans un nouvel onglet" checked={targetBlank} onChange={setTargetBlank} />
            </PanelRow>
        </PanelBody>
    );
};

export const getIdAndTitle = (el) => {
    var label = he.decode(el.title.rendered);
    if (el.sku) label += ' (' + el.sku + ')';
    return {
        value: el.id,
        label: label,
    };
};

export const getIdAndName = (el) => {
    return {
        value: el.id,
        label: he.decode(el.name),
    };
};

export const getSlugAndName = (el) => {
    return {
        value: el.slug,
        label: he.decode(el.name),
    };
};
